// src/utils/localDate.ts
// QUEUE-UI-A (21.09.2026): дословный перенос серверных функций
// computeLocalDate/computeStreak (api/index.js:383-456) на клиент — очередь
// (utils/queue.ts) и DashboardScreen должны считать дату/стрик записи
// офлайн-очереди ТЕМ ЖЕ способом, каким сервер посчитает её при доставке,
// иначе прогноз на экране разойдётся с тем, что сервер запишет в
// daily_completions.local_date.
//
// Почему не переиспользованы три существующие клиентские реализации
// (HomeScreen.computeStartOfCourseDay, components/common/Countdown,
// utils/time.ts): все три решают другую задачу — таймер обратного отсчёта
// и подсветку "сегодня" на витрине, не запись даты в БД — и ни одна не
// сверена с серверной арифметикой (QUEUE-UI-RECON, Шаг 1). Их поведение
// известно и обкатано для своих экранов; переиспользование под новую задачу
// потребовало бы отдельной сверки каждой из трёх, тогда как перенос
// серверных функций сверяется один раз с их же источником истины.
//
// content-access, инвариант 8: то, что здесь вычисляется — темп/отображение
// (какой день показать закрашенным офлайн), не право на контент. Право
// проверяет сервер заново при каждой доставке записи из очереди
// (queue.ts — комментарий в шапке файла).

// YYYY-MM-DD -> предыдущий день (строковая арифметика, без повторного
// прогона через TZ — см. комментарий у серверного computeLocalDate ниже).
export function prevYMD(ymd: string): string {
  const [y, m, d] = ymd.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() - 1);
  const yy = dt.getUTCFullYear();
  const mm = String(dt.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(dt.getUTCDate()).padStart(2, "0");
  return `${yy}-${mm}-${dd}`;
}

type TZParts = { y: number; m: number; d: number; h: number; i: number };

export function partsInTZ(timezone: string, base: Date): TZParts {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: timezone,
    hourCycle: "h23",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).formatToParts(base);
  const map: Record<string, string> = {};
  for (const p of parts) map[p.type] = p.value;
  return {
    y: Number(map.year || "1970"),
    m: Number(map.month || "1"),
    d: Number(map.day || "1"),
    h: Number(map.hour || "0"),
    i: Number(map.minute || "0"),
  };
}

// Эффективная календарная дата с учётом порога H:M в таймзоне юзера.
// Отличие от клиентского ymdEffective (устаревшее сравнение, не этот файл):
// шаг "предыдущий день" делается строковой арифметикой (prevYMD), а не
// повторным прогоном исходной UTC-полуночи через TZ — вариант с повторным
// прогоном даёт минус двое суток для TZ западнее UTC. Для RU-зон (UTC+) оба
// варианта совпадают, поэтому расхождение не было замечено раньше.
export function computeLocalDate(
  at: Date | string,
  tz: string,
  morning_hour: number | null | undefined,
  morning_minute: number | null | undefined
): string {
  const base = at instanceof Date ? at : new Date(at);
  const cur = partsInTZ(tz, base);
  const H = Number(morning_hour) || 0;
  const M = Number(morning_minute) || 0;
  const todayYmd = `${cur.y}-${String(cur.m).padStart(2, "0")}-${String(cur.d).padStart(2, "0")}`;
  if (cur.h < H || (cur.h === H && cur.i < M)) {
    return prevYMD(todayYmd);
  }
  return todayYmd;
}

// Текущий стрик (от todayYmd назад, пока даты подряд) + лучший стрик за всё
// время (перенос loadStreak/computeStreak, api/index.js).
export function computeStreak(datesArr: string[], todayYmd: string): { streak: number; best: number } {
  const set = new Set(datesArr);
  let cur = todayYmd;
  let streak = 0;
  while (set.has(cur)) {
    streak += 1;
    cur = prevYMD(cur);
  }

  const uniqAsc = Array.from(set).sort();
  let best = 0;
  let run = 0;
  let prev: string | null = null;
  for (const d of uniqAsc) {
    if (prev && prevYMD(d) === prev) run += 1;
    else run = 1;
    if (run > best) best = run;
    prev = d;
  }

  return { streak, best };
}
